import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { css } from 'linaria'
import {
  Price,
  ImagePreview,
  Input,
} from '@hb/web-components'
import { formatCurrency, formatDate } from '@store'
import useTrackEvents from '@hooks/useTrackEvents/useTrackEvents'
import E from '@hooks/useTrackEvents/events'

import { useTranslation } from '@/hooks/I18nProvider'

const styles = css`
  @apply flex flex-col;
  min-width: 300px;
  z-index: 2;
  padding-top: 0 !important;

  .products-container {
    @apply flex flex-col gap-y-4 max-h-80 overflow-y-auto pr-1;
  }

  .tr {
    @apply flex items-center bg-mercury-400 px-3 rounded-lg shadow-sm md:py-1 md:flex-wrap;
    
    &.empty {
      @apply border-none mt-4;
    }
    &.child {
      @apply w-full border-b-0 mt-0;
      .td {
        @apply pt-0;
          &.name {
          @apply pl-5;
        }
      }
    }
  }

  .td {
    @apply py-1 md:py-2;

    img {
      @apply rounded max-w-full;
      @media (min-width: 768px) {
        max-height: 70px;
      }
    }
  }

  .tr {
    .td.image {
      @apply w-auto;
      
      & > img.empty {
        background-color: #f8f4fd;
      }
    }

    .td.name {
      @apply flex-grow pl-3 flex flex-col justify-center text-gray-700 text-sm md:text-base;
      flex-basis: 0;

      .info {
        @apply font-normal mt-1 text-gray-500 md:mt-2;
        p {
          @apply flex items-center mt-1;
          img { @apply w-3.5 h-3.5 mr-2; }
        }
      }
    }
    
    .td.quantity {
      @apply block text-sm text-gray-500 text-right pr-2 w-10 md:hidden;
    }

    .quantity-desktop {
      @apply hidden md:block text-sm text-gray-500 mt-1 md:font-bold;
    }

    .td.money {
      @apply text-gray-700 w-20 text-right md:font-bold;
      min-width: auto;
      @media (min-width: 768px) {
        min-width: 80px;
      }
    }
  }

  .custom-fields-container {
    @apply bg-mercury-400 px-3 py-2 rounded-lg mt-4 pt-4 text-sm text-gray-600 flex flex-col gap-1;
    h2 {
      @apply text-sm text-gray-500 font-bold pb-1;
    }
    .custom-field-row {
      @apply flex justify-between mt-1 pr-1 text-gray-700;
      .field-label {
        @apply font-medium mr-2;
      }
      .field-value {
        @apply text-right;
      }
    }
  }
  
  .break {
    flex-basis: 100%;
    height: 0;
  }

  .broken {
    @apply flex font-bold text-gray-700;
    flex-grow: 1;
    justify-content: flex-end;
    padding-top: 0;
    padding-right: 20px;
  }

  .make-savings-green {
    &.tr .td {
      &.name,
      &.money {
        color: #15a292;
      }
    }
  }

  .effects, .total-section {
    @apply flex flex-col gap-y-1 mt-4 text-sm text-gray-700;
  }

  .effects > .item, .total-section > .row {
    @apply flex justify-between items-center;
  }

  .effects > .item .value, .total-section > .row .value {
    @apply font-bold;
  }
  
  .effects > .item {
    @apply md:py-4 md:px-2 md:border-b md:border-gray-200;
  }

  .total-section > .row.main-total {
    @apply px-4 mt-2;
    .label {
      @apply text-base md:text-lg md:font-bold;
    }
    .value {
      @apply font-bold text-xl md:text-2xl;
    }
  }

  .total {
    @apply hidden;
  }
  
  div.aditional-data { 
    @apply flex justify-between items-center mt-10 w-full; 

    & > div.can-edit {
      @apply text-sm;
      &.left {
        @apply text-left;
        a {
            @apply flex items-center text-gray-500 cursor-pointer hover:text-n1co-carbon-500;
            img {
                @apply mr-2 w-4 h-4;
            }
        }
      }

      &.right {
         @apply text-right ml-auto;
         & > a { 
            @apply p-2 px-5 text-blue-600 hover:text-blue-800 inline-flex items-center rounded-lg cursor-pointer; 
            span { 
              @apply flex items-center justify-center bg-blue-600 rounded-full px-1 w-4 h-4 mr-2 text-gray-200; 
            } 
          }
      }
    } 
  }
  .input-additional-notes { 
    @apply transition-all duration-500 ease-in-out max-h-0 overflow-hidden mt-2; 
    &.unfolded { 
      @apply overflow-y-auto max-h-screen; 
    } 
  }
`

const Table = ({
  products,
  total,
  totalSubtext,
  currencyLocale,
  currencyCode,
  hasVariableAmount,
  customFields,
  customFieldsDesc,
  canEditCustomData,
  canEditCustomDataFn,
  showNotes,
  orderNotes,
  onChangeOrderNotes,
  discounts = [],
  surcharges = [],
}) => {
  const { t } = useTranslation()
  const { trackEvent } = useTrackEvents()
  const productsWithImages = products
    .filter((p) => !!p.productImageUrl)
    .sort((p) => p.name)
  const productsWithoutImages = products
    .filter((p) => !p.productImageUrl)
    .sort((p) => p.name)
  const orderedProducts = [...productsWithImages, ...productsWithoutImages]

  let totalSavings = 0
  const [unfoldedNotes, setUnfoldedNotes] = useState(false)

  const fields = customFieldsDesc.filter((f) => f.isVisible).map((c, i) => ({ ...c, ...customFields[i] }))

  return (
    <section className={styles}>
      <div className="products-container">
        {orderedProducts.map((product) => {
          const price = product.promoPrice || product.price
          if (product.promoPrice) {
            totalSavings
              += (product.price - product.promoPrice) * product.quantity
          }
          const tot = (
            <Price
              prevprice={
                product.promoPrice ? formatCurrency(product.price, currencyLocale, currencyCode) : null
              }
            >
              {formatCurrency(price, currencyLocale, currencyCode)}
            </Price>
          )
          const tooLarge = tot.length > 10

          return (
            <Fragment key={product.itemId}>
              <div className="tr">
                <div className="td image">
                  {product.productImageUrl ? (
                    <ImagePreview
                      src={product.productImageUrl}
                      hdSrc={product.productImageUrl}
                    />
                  ) : (
                    <img className="empty" src="/images/product-unnamed.svg" />
                  )}
                </div>
                <div className="td name">
                  <p>{product.name}</p>
                  {
                    product.quantity > 0 && (
                      <div className="quantity-desktop">
                        x
                        {product.quantity}
                      </div>
                    )
                  }
                  {
                    product?.isSubscription && (
                      <span className="info">
                        {product.trialCyclesNumber > 0 && (
                          <p>
                            <img src="/images/outline-check.svg" />
                            {`${product.trialCyclesNumber} ${product.trialCycleType} ${t`table.trial`}`}
                          </p>
                        )}
                        {product.cyclesToBillBeforeAllowCancelation > 0 && (
                          <p>
                            <img src="/images/outline-check.svg" />
                            {`${t`table.minimumCycles`} ${product.cyclesToBillBeforeAllowCancelation} ${product.billingCycleType}`}
                          </p>
                        )}
                        {product.enrollmentEndDate && (
                          <p>
                            <img src="/images/outline-check.svg" />
                            {`${t`table.enrollmentEnd`} ${formatDate(product.enrollmentEndDate, 'MMMM YYYY')}`}
                          </p>
                        )}
                      </span>
                    )
                  }
                </div>
                {
                  product.quantity && (
                    <div className="td quantity">
                      x
                      {product.quantity}
                    </div>
                  )
                }

                {tooLarge ? (
                  <>
                    <div className="break" />
                    <div className="td broken">{tot}</div>
                  </>
                ) : (
                  !hasVariableAmount && (
                    <div className="td money">{tot}</div>
                  )
                )}
              </div>
              {
                (product.modifiers && product.modifiers.length > 0)
                && product.modifiers.map((modifier) => modifier.selectedOptions.map((option) => (
                  <div className="tr child" key={`${option.selectedOptionId} - ${option.name}`}>
                    <div className="td image" />
                    <div className="td name">{`• ${option.name}`}</div>
                    <div className="td quantity" />
                    <div className="td money">{`+ ${formatCurrency(option.amount)}`}</div>
                  </div>
                )))
              }
            </Fragment>
          )
        })}
        {fields.length > 0 && (
          <div className="custom-fields-container">
            <h2>Campos personalizados</h2>
            {fields.map((field) => (
              <div className="custom-field-row" key={`custom-field-${field.name}`}>
                <span className="field-label">
                  {field.label}
                  :
                </span>
                <span className="field-value">{field.value || '-'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {products.length === 0 && (
        <div className="tr empty">{t`table.empty`}</div>
      )}
      {totalSavings > 0 && (
        <div className="tr make-savings-green">
          <div className="td name">{t`table.savedTotal`}</div>
          <div className="td money savings">{formatCurrency(totalSavings, currencyLocale, currencyCode)}</div>
        </div>
      )}

      <div className="total-section">
        {(surcharges?.length > 0 || discounts?.length > 0) && (
          <div className="effects">
            {surcharges.map((surcharge) => (
              <div className="item" key={surcharge.id}>
                <div className="label">{surcharge.name}</div>
                <div className="value">{`+ ${formatCurrency(surcharge.amount, currencyLocale, currencyCode)}`}</div>
              </div>
            ))}
            {discounts.map((discount) => (
              <div className="item" key={discount.id}>
                <div className="label">{discount.name}</div>
                <div className="value">{`- ${formatCurrency(discount.amount, currencyLocale, currencyCode)}`}</div>
              </div>
            ))}
          </div>
        )}

        <div className="row main-total">
          <div className="label">{`${t`table.total`} ${totalSubtext && `(${totalSubtext})`}`}</div>
          {!hasVariableAmount ? (
            <div className="value">{formatCurrency(total, currencyLocale, currencyCode)}</div>
          ) : (
            <div className="value">{t`table.pending`}</div>
          )}
        </div>
      </div>

      <div className="total" />

      <div className="aditional-data">
        {canEditCustomData && (
        <div className="can-edit left">
          <a onClick={canEditCustomDataFn}>
            <img src="/images/edit-icon.svg" />
            {t`table.editOrderData`}
          </a>
        </div>
        )}
        {showNotes && (
          <div className="can-edit right">
            <a onClick={() => {
              setUnfoldedNotes(!unfoldedNotes)
              trackEvent(E.SET_NOTES_IN_PAYMENT, {
                isActive: !unfoldedNotes,
              })
            }}
            >
              <span className="plus">{unfoldedNotes ? '-' : '+'}</span>
              {`${t`total.addOrderNotes`}`}
            </a>
          </div>
        )}
      </div>
      {showNotes && (
        <div className={`input-additional-notes${unfoldedNotes ? ' unfolded' : ''}`}>
          <Input
            name="checkoutNote"
            type="textarea"
            placeholder={t`total.addOrderNotesPlaceholder`}
            onChange={(e) => onChangeOrderNotes(e.target.value)}
            value={orderNotes}
            multiple
          />
        </div>
      )}
    </section>
  )
}

Table.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      productImageUrl: PropTypes.string,
      name: PropTypes.string,
      quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      subTotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      price: PropTypes.number,
      isSubscription: PropTypes.bool,
      billingCyclesNumber: PropTypes.number,
      trialCyclesNumber: PropTypes.number,
      billingCycleType: PropTypes.string,
      trialCycleType: PropTypes.string,
    }),
  ),
  total: PropTypes.number,
  currencyCode: PropTypes.string,
  currencyLocale: PropTypes.string,
  hasVariableAmount: PropTypes.bool.isRequired,
  customFields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string,
    }),
  ),
  canEditCustomData: PropTypes.bool,
  canEditCustomDataFn: PropTypes.func,
  showNotes: PropTypes.bool,
  orderNotes: PropTypes.string,
  onChangeOrderNotes: PropTypes.func,
  totalSubtext: PropTypes.string,
  discounts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      amount: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  surcharges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      amount: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  customFieldsDesc: PropTypes.arrayOf(
    PropTypes.shape({
      defaultValue: PropTypes.string,
      isEditable: PropTypes.bool,
      isRequired: PropTypes.bool,
      isVisible: PropTypes.bool,
      label: PropTypes.string,
      name: PropTypes.string,
      placeholder: PropTypes.string,
    }),
  ),
}

Table.defaultProps = {
  products: [],
  total: 0,
  currencyCode: null,
  currencyLocale: null,
  customFields: [],
  canEditCustomData: false,
  canEditCustomDataFn: () => {},
  showNotes: true,
  orderNotes: '',
  onChangeOrderNotes: () => {},
  totalSubtext: '',
  discounts: [],
  surcharges: [],
  customFieldsDesc: [],
}

export default Table
